import React, { useEffect, useState } from 'react';
import { Container, Box, Typography } from '@mui/material';
import ApiService from '../../services/api';

const TermsOfService = () => {
  const lastUpdated = 'November 2025';
  const [systemSettings, setSystemSettings] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const settings = await ApiService.getSystemSettings();
        if (!mounted) return;
        setSystemSettings(settings || null);
        try { localStorage.setItem('systemSettings', JSON.stringify(settings)); } catch (_) {}
        try { localStorage.setItem('adminSettings', JSON.stringify(settings)); } catch (_) {}
      } catch (_) {
        try {
          const raw = localStorage.getItem('systemSettings') || localStorage.getItem('adminSettings');
          const parsed = raw ? JSON.parse(raw) : null;
          if (mounted) setSystemSettings(parsed);
        } catch (_) {}
      }
    })();
    // Listen for admin settings updates to refresh content live
    const onSettingsUpdated = (e) => {
      const detail = e?.detail || null;
      if (detail && mounted) {
        setSystemSettings(detail);
      } else {
        // Fallback: try localStorage if available
        try {
          const raw = localStorage.getItem('adminSettings');
          if (raw && mounted) setSystemSettings(JSON.parse(raw));
        } catch (_) {}
      }
    };
    try { window.addEventListener('systemSettingsUpdated', onSettingsUpdated); } catch (_) {}
    return () => { mounted = false; };
  }, []);

  const adminTerms = (systemSettings?.legal?.termsOfService || '').trim();

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box>
        <Typography variant="h3" gutterBottom>
          Terms of Service
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last updated: {lastUpdated}
        </Typography>
        {adminTerms ? (
          <Typography paragraph sx={{ whiteSpace: 'pre-wrap' }}>
            {adminTerms}
          </Typography>
        ) : (
          <>
        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Acceptance of Terms
        </Typography>
        <Typography paragraph>
          By accessing or using the Job Portal platform (“Service”), you agree to these Terms of Service and our Privacy Policy. If you do not agree, do not use the Service.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Accounts & Eligibility
        </Typography>
        <Typography paragraph>
          You are responsible for maintaining the confidentiality of your account and for all activities under it. You must provide accurate information. We may suspend or terminate accounts that violate these terms or applicable law.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Use of the Service
        </Typography>
        <Typography paragraph>
          Employers may post jobs and contact job seekers subject to applicable laws and these Terms. Job seekers may create profiles, upload resumes, and apply to listings. You agree not to misuse the Service, including but not limited to scraping, spamming, interfering with security features, or posting unlawful content.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Paid Features & Contact Requests
        </Typography>
        <Typography paragraph>
          The Service may offer paid features (e.g., employer credits to request job seeker contact details). Payment obligations are binding once a purchase is made. Contact details are disclosed only as outlined in the Privacy Policy and must be used solely for legitimate recruitment related to the specific listing or interaction. Resale, unauthorized sharing, or marketing use of contact information is prohibited and may result in immediate suspension without refund.
        </Typography>
        <Typography paragraph>
          Except as required by law, fees are non‑refundable once access is granted or credits are used. We may issue refunds at our discretion for unused purchases where access was not provided due to a platform error.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Content & License
        </Typography>
        <Typography paragraph>
          You retain rights to content you submit. By posting content, you grant us a non‑exclusive, worldwide, royalty‑free license to host, display, and distribute such content solely to provide and improve the Service.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Prohibited Conduct
        </Typography>
        <Typography paragraph>
          You agree not to: (a) post false or misleading jobs or profiles; (b) engage in discrimination or harassment; (c) scrape or harvest data; (d) attempt to bypass access controls; (e) infringe any intellectual property or privacy rights.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Data & Privacy
        </Typography>
        <Typography paragraph>
          Our collection and use of personal information is described in the Privacy Policy. We do not sell personal information. Limited disclosure may occur to deliver features you choose (e.g., paid contact requests) under strict usage restrictions.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Third‑Party Services
        </Typography>
        <Typography paragraph>
          The Service may integrate third‑party providers (payments, hosting, analytics, communications). We are not responsible for third‑party services’ availability or practices, but we take reasonable steps to ensure appropriate contractual protections.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Disclaimer of Warranties
        </Typography>
        <Typography paragraph>
          The Service is provided “as is” and “as available.” We disclaim all warranties to the fullest extent permitted by law, including implied warranties of merchantability, fitness for a particular purpose, and non‑infringement.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Limitation of Liability
        </Typography>
        <Typography paragraph>
          To the maximum extent permitted by law, we are not liable for indirect, incidental, consequential, special, or exemplary damages, including lost profits, arising from or related to your use of the Service. Our aggregate liability for any claim shall not exceed the greater of: (i) the amounts you paid to us in the 12 months prior to the claim, or (ii) USD 100.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Indemnification
        </Typography>
        <Typography paragraph>
          You agree to indemnify and hold harmless the platform, its affiliates, and personnel from any claims, damages, liabilities, and expenses (including legal fees) arising from your content, your use of the Service, or your violation of these Terms or applicable law.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Termination
        </Typography>
        <Typography paragraph>
          We may suspend or terminate access immediately for violations of these Terms, fraudulent or abusive activity, or where required by law. You may stop using the Service at any time.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Governing Law & Dispute Resolution
        </Typography>
        <Typography paragraph>
          These Terms are governed by the laws of [Your Jurisdiction]. Any dispute shall be resolved in the courts located in [Your Venue], unless arbitration is required by local law. You agree to the exclusive jurisdiction and venue of those courts.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Changes to Terms
        </Typography>
        <Typography paragraph>
          We may update these Terms periodically. Continued use of the Service after changes indicates acceptance of the updated Terms.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Contact Us
        </Typography>
        <Typography paragraph>
          For questions about these Terms, contact: legal@jobitzm.com or jobitzm@gmail.com.
        </Typography>
          </>
        )}
      </Box>
    </Container>
  );
};

export default TermsOfService;